/**
 * StayInfoCard — Guest-Portal-only Stay-Info (WLAN, Hausordnung, Notruf).
 *
 * Die Anfahrt steht nicht mehr hier: sie ist eine gepflegte Schrittfolge am Objekt
 * (ArrivalGuideEditor) und nicht ein Freitextfeld je Apartment.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Wifi } from 'lucide-react';
import { Button, FormField, Input, Pill, Textarea } from '@/shared/components';
import { toApiError, useApi, type UpdatePropertyStayInfoRequest } from '@/shared/api';
import { PropertyFormSectionHeader } from '../property-form/ChannelsPushedSection';
import { useApartmentDetail } from './ApartmentDetailLayout';

export function StayInfoCard() {
  const { propertyId } = useApartmentDetail();
  const api = useApi();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['property', propertyId],
    queryFn: () => api.properties.get(propertyId),
  });

  const [form, setForm] = useState<UpdatePropertyStayInfoRequest>({
    wiFiSsid: '',
    wiFiPassword: '',
    emergencyContact: '',
    apartmentDescriptionDe: '',
    apartmentDescriptionEn: '',
  });

  useEffect(() => {
    if (data) {
      setForm({
        wiFiSsid: data.wiFiSsid ?? '',
        // Backend liefert das Passwort nie zurück (Secret-Hygiene). Leer = unverändert.
        wiFiPassword: '',
        emergencyContact: data.emergencyContact ?? '',
        apartmentDescriptionDe: data.apartmentDescriptionDe ?? '',
        apartmentDescriptionEn: data.apartmentDescriptionEn ?? '',
      });
    }
  }, [data]);

  const save = useMutation({
    mutationFn: () =>
      api.properties.updateStayInfo(propertyId, {
        wiFiSsid: form.wiFiSsid || null,
        // null = unverändert (Edit-Pattern); leerer String würde „löschen" bedeuten,
        // Versehens-Löschungen durch leeres Feld vermeiden.
        wiFiPassword: form.wiFiPassword ? form.wiFiPassword : null,
        emergencyContact: form.emergencyContact || null,
        apartmentDescriptionDe: form.apartmentDescriptionDe || null,
        apartmentDescriptionEn: form.apartmentDescriptionEn || null,
      }),
    onSuccess: () => {
      toast.success('Stay-Info gespeichert');
      setForm((f) => ({ ...f, wiFiPassword: '' }));
      void qc.invalidateQueries({ queryKey: ['property', propertyId] });
    },
    onError: async (err) => {
      const e = await toApiError(err);
      toast.error(e.detail ?? e.title);
    },
  });

  if (isLoading || !data) {
    return null;
  }

  return (
    <div className="bg-card border-line/50 shadow-card rounded-lg border p-6">
      <div className="mb-6 flex items-start justify-between gap-3">
        <PropertyFormSectionHeader
          icon={<Wifi size={13} strokeWidth={2} />}
          iconTint="sage"
          title="Stay-Info"
          subtitle="Nur im Gast-Portal sichtbar — nicht an Channex oder OTAs."
        />
        {data.hasWiFiPassword && (
          <Pill variant="live" size="sm">
            WLAN-PW hinterlegt
          </Pill>
        )}
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <FormField label="WLAN-SSID">
          <Input
            value={form.wiFiSsid ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, wiFiSsid: e.target.value }))}
            placeholder="Meine Fritzbox"
          />
        </FormField>
        <FormField
          label={data.hasWiFiPassword ? 'WLAN-Passwort (leer = unverändert)' : 'WLAN-Passwort'}
        >
          <Input
            type="password"
            value={form.wiFiPassword ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, wiFiPassword: e.target.value }))}
            placeholder={data.hasWiFiPassword ? '••••••••' : 'Neues WLAN-Passwort'}
          />
        </FormField>
        <FormField
          label="Wo liegt dieses Apartment? (deutsch)"
          hint="Steht im Gastportal zwischen Haustür- und Schlüssel-Code — z. B. „Apartment 05 im Obergeschoss, Code in der Schlüsselbox eingeben.“"
          className="md:col-span-2"
        >
          <Textarea
            value={form.apartmentDescriptionDe ?? ''}
            rows={2}
            onChange={(e) => setForm((f) => ({ ...f, apartmentDescriptionDe: e.target.value }))}
            placeholder="Apartment 05 im 1. Obergeschoss, links. Code in der Schlüsselbox neben der Tür eingeben."
          />
        </FormField>
        <FormField
          label="Wo liegt dieses Apartment? (englisch)"
          hint="Fehlt sie, bekommt auch ein englischer Gast den deutschen Text — besser als gar keiner."
          className="md:col-span-2"
        >
          <Textarea
            value={form.apartmentDescriptionEn ?? ''}
            rows={2}
            onChange={(e) => setForm((f) => ({ ...f, apartmentDescriptionEn: e.target.value }))}
            placeholder="Apartment 05 on the first floor, on the left. Enter the code in the key box next to the door."
          />
        </FormField>
        <FormField
          label="Notruf / Vermieter-Telefon"
          hint="Für Notfälle während des Aufenthalts."
          className="md:col-span-2"
        >
          <Input
            value={form.emergencyContact ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, emergencyContact: e.target.value }))}
            placeholder="+49 …"
          />
        </FormField>
      </div>

      <div className="mt-6 flex justify-end">
        <Button variant="brand" onClick={() => save.mutate()} loading={save.isPending}>
          Stay-Info speichern
        </Button>
      </div>
    </div>
  );
}
